/**
 * Print-Only Personal Envisioning Page for PDF Generation
 * Renders personal envisioning data (Sections A-O) for PDF export
 *
 * URL: /print/personal-envisioning
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';
import { formatPercentage, formatCurrency } from '../../utils/enhancedDashboardUtils';
import { PERSONAL_CATEGORY_KEY_MAP, PERSONAL_CATEGORY_DESCRIPTIONS } from '../../utils/categoryDescriptions';
import { SCORE_THRESHOLDS } from '../../utils/enhancedDashboardConstants';

// PDF-specific color functions that return hex codes instead of Tailwind classes
const getPdfScoreColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return '#8b6f47';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return '#16a34a'; // green-600
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return '#2563eb'; // blue-600
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return '#ca8a04'; // yellow-600
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return '#ea580c'; // orange-600
  return '#dc2626'; // red-600
};

const getPdfScoreBgColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return '#f5e6d3';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return '#f0fdf4'; // green-50
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return '#eff6ff'; // blue-50
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return '#fefce8'; // yellow-50
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return '#fff7ed'; // orange-50
  return '#fef2f2'; // red-50
};

const PrintPersonalEnvisioning = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [envisioningData, setEnvisioningData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [wealthGap, setWealthGap] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter (for PDF generation) or localStorage (for direct viewing)
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch all required data in parallel
        const [envisioningResponse, profileResponse, assessmentResponse, wealthGapResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-envisioning`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/personal-assessment/current`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/wealth-gap`, { headers })
        ]);

        if (!envisioningResponse.ok) throw new Error('Failed to fetch envisioning data');
        if (!profileResponse.ok) throw new Error('Failed to fetch profile');

        const envisioningJson = await envisioningResponse.json();
        const profileJson = await profileResponse.json();

        setEnvisioningData(envisioningJson);
        setProfileData(profileJson.data.profile);

        // Parse assessment data
        let assessmentJson = null;
        if (assessmentResponse.ok) {
          assessmentJson = await assessmentResponse.json();
          if (assessmentJson && assessmentJson.id) {
            setAssessmentData(assessmentJson);
          }
        }

        // Parse wealth gap data
        let wealthJson = null;
        if (wealthGapResponse.ok) {
          wealthJson = await wealthGapResponse.json();
          if (wealthJson.wealth_gap) {
            setWealthGap(wealthJson.wealth_gap);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('[PrintPersonalEnvisioning] Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && envisioningData) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title
      const clientName = [
        profileData?.client_prefix,
        profileData?.client_first_name,
        profileData?.client_last_name,
        profileData?.client_suffix
      ].filter(Boolean).join(' ') || 'Client';
      document.title = `${clientName} - Personal Envisioning Report`;
    }
  }, [loading, envisioningData, profileData]);

  // Get top strengths and concerns from personal assessment
  const getTopStrengthsAndConcerns = () => {
    if (!assessmentData?.category_scores) {
      return { strengths: [], concerns: [] };
    }

    const categories = Object.entries(assessmentData.category_scores || {})
      .map(([key, score]) => {
        const displayName = PERSONAL_CATEGORY_KEY_MAP[key];
        const description = PERSONAL_CATEGORY_DESCRIPTIONS[displayName];
        return {
          key,
          score,
          name: displayName,
          summary: description?.summary || ''
        };
      })
      .filter(cat => cat.score !== null && cat.score !== undefined);

    // Sort by score descending
    const sorted = [...categories].sort((a, b) => b.score - a.score);

    // Get top 3 (highest scores) and bottom 3 (lowest scores)
    const strengths = sorted.slice(0, 3);
    const concerns = sorted.slice(-3).reverse();

    return { strengths, concerns };
  };

  const { strengths: autoStrengths, concerns: autoConcerns } = getTopStrengthsAndConcerns();

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading personal envisioning data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const data = envisioningData || {};

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const parseJSON = (field) => {
    try {
      return typeof field === 'string' ? JSON.parse(field) : field;
    } catch {
      return field || [];
    }
  };

  // Normalization functions for converting database values to human-readable labels
  const normalizeRiskTolerance = (value) => {
    const mapping = {
      'conservative': 'Conservative',
      'moderate': 'Moderate',
      'aggressive': 'Aggressive',
      'very_aggressive': 'Very Aggressive'
    };
    return mapping[value] || value;
  };

  const normalizeWealthStrategy = (value) => {
    const mapping = {
      'business_sale': 'Business Sale Proceeds',
      'investments': 'Investment Portfolio Growth',
      'real_estate': 'Real Estate Investments',
      'passive_income': 'Passive Income Streams',
      'inheritance': 'Expected Inheritance',
      'continued_earnings': 'Continued Earnings Post-Exit',
      'combination': 'Combination of Multiple Strategies',
      'other': 'Other'
    };
    return mapping[value] || value;
  };

  const normalizePlanningStatus = (value) => {
    const mapping = {
      'no_plan': 'No Formal Plan',
      'diy': 'DIY Planning',
      'working_with_advisor': 'Working with Advisor',
      'comprehensive': 'Comprehensive Plan'
    };
    return mapping[value] || value;
  };

  const normalizeEstateDocuments = (docs) => {
    const mapping = {
      'will': 'Last Will and Testament',
      'trust': 'Revocable Living Trust',
      'poa': 'Durable Power of Attorney',
      'healthcare_directive': 'Healthcare Directive / Living Will',
      'beneficiary_designations': 'Updated Beneficiary Designations',
      'guardianship': 'Guardianship Designations'
    };
    return docs.map(doc => mapping[doc] || doc);
  };

  const normalizeSourceOfWealth = (sources) => {
    const mapping = {
      'business_ownership': 'Business Ownership',
      'employment_income': 'Employment/Salary Income',
      'investments': 'Investment Portfolio',
      'real_estate': 'Real Estate Holdings',
      'inheritance': 'Inheritance/Family Wealth',
      'entrepreneurship': 'Entrepreneurial Ventures',
      'professional_practice': 'Professional Practice (Law, Medical, etc.)',
      'royalties_ip': 'Royalties/Intellectual Property',
      'other': 'Other'
    };
    return sources.map(source => mapping[source] || source);
  };

  const normalizeUseOfWealth = (uses) => {
    const mapping = {
      'lifestyle_support': 'Lifestyle Support & Living Expenses',
      'family_security': 'Family Security & Education',
      'reinvestment': 'Reinvestment & Wealth Growth',
      'philanthropy': 'Philanthropy & Charitable Giving',
      'legacy_building': 'Legacy Building & Generational Wealth',
      'retirement_planning': 'Retirement Planning & Independence',
      'business_expansion': 'Business Expansion & Ventures',
      'experiences_travel': 'Experiences & Travel',
      'other': 'Other'
    };
    return uses.map(use => mapping[use] || use);
  };

  // Parse JSON fields
  const estateDocuments = parseJSON(data.estate_planning_documents) || [];
  const sourceOfWealth = parseJSON(data.source_of_wealth) || [];
  const useOfWealth = parseJSON(data.use_of_wealth) || [];
  const fiveDsPersonalDeathItems = parseJSON(data.five_ds_personal_death_items) || [];
  const fiveDsPersonalDisabilityItems = parseJSON(data.five_ds_personal_disability_items) || [];
  const fiveDsPersonalDivorceItems = parseJSON(data.five_ds_personal_divorce_items) || [];
  const fiveDsPersonalDistressItems = parseJSON(data.five_ds_personal_distress_items) || [];
  const fiveDsPersonalDisagreementItems = parseJSON(data.five_ds_personal_disagreement_items) || [];
  const personalSprintPriorities = parseJSON(data.personal_sprint_priorities) || ['', '', ''];
  const personalQuickWins = parseJSON(data.personal_quick_wins) || ['', '', ''];

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Personal Envisioning Report</h1>
          <div className="report-type">3-10 Year Personal Vision & Life Plan</div>
        </div>

        <div className="cover-business-info">
          <h2>
            {profileData?.client_prefix && `${profileData.client_prefix} `}
            {profileData?.client_first_name || ''} {profileData?.client_last_name || ''}
            {profileData?.client_suffix && ` ${profileData.client_suffix}`}
            {!profileData?.client_first_name && !profileData?.client_last_name && 'Client Name Not Set'}
          </h2>
          {data.current_age && <p className="industry">Age: {data.current_age}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Personal Envisioning Report</p>
        </div>
      </div>

      {/* Section A: Current Personal Snapshot */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section A: Current Personal Snapshot</h1>
        <p className="section-subtitle">Where are you today in life?</p>

        {/* Gap Assessment Scores */}
        {assessmentData && assessmentData.answered_questions > 0 ? (
          <div className="content-block" style={{ marginBottom: '0.15in' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in' }}>Gap Assessment Scores</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.1in' }}>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.overall_score || 0) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.overall_score || 0), marginBottom: '0.05in' }}>
                  {formatPercentage(assessmentData.overall_score || 0)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Overall</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.financial_score || 0) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.financial_score || 0), marginBottom: '0.05in' }}>
                  {formatPercentage(assessmentData.financial_score || 0)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Financial</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.wellbeing_score || 0) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.wellbeing_score || 0), marginBottom: '0.05in' }}>
                  {formatPercentage(assessmentData.wellbeing_score || 0)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Wellbeing</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.spiritual_score || 0) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.spiritual_score || 0), marginBottom: '0.05in' }}>
                  {formatPercentage(assessmentData.spiritual_score || 0)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Spiritual</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="content-block" style={{ marginBottom: '0.15in', padding: '0.15in', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', textAlign: 'center' }}>
            <p style={{ fontSize: '8pt', color: '#6b7280' }}>No assessment data available</p>
          </div>
        )}

        {/* Top 3 Strengths */}
        {autoStrengths.length > 0 && (
          <div className="content-block" style={{ marginBottom: '0.15in' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in' }}>Top 3 Personal Strengths</h3>
            {autoStrengths.map((strength, idx) => (
              <div key={idx} style={{ padding: '0.1in', marginBottom: '0.08in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(strength.score) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.05in' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: getPdfScoreColor(strength.score), color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8pt', fontWeight: 'bold', marginRight: '0.1in' }}>
                      {idx + 1}
                    </div>
                    <span style={{ fontSize: '8pt', fontWeight: '600' }}>{strength.name}</span>
                  </div>
                  <span style={{ fontSize: '10pt', fontWeight: 'bold', color: getPdfScoreColor(strength.score) }}>{formatPercentage(strength.score)}</span>
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', marginLeft: '0.3in' }}>{strength.summary}</p>
              </div>
            ))}
          </div>
        )}

        {/* Top 3 Concerns */}
        {autoConcerns.length > 0 && (
          <div className="content-block" style={{ marginBottom: '0.15in' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in' }}>Top 3 Personal Concerns/Gaps</h3>
            {autoConcerns.map((concern, idx) => (
              <div key={idx} style={{ padding: '0.1in', marginBottom: '0.08in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(concern.score) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.05in' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: getPdfScoreColor(concern.score), color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8pt', fontWeight: 'bold', marginRight: '0.1in' }}>
                      {idx + 1}
                    </div>
                    <span style={{ fontSize: '8pt', fontWeight: '600' }}>{concern.name}</span>
                  </div>
                  <span style={{ fontSize: '10pt', fontWeight: 'bold', color: getPdfScoreColor(concern.score) }}>{formatPercentage(concern.score)}</span>
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', marginLeft: '0.3in' }}>{concern.summary}</p>
              </div>
            ))}
          </div>
        )}

        {/* Basic Info */}
        <div className="content-block">
          <table className="info-table">
            <tbody>
              <tr>
                <th>Current Age</th>
                <td>{data.current_age || 'Not specified'}</td>
              </tr>
              <tr>
                <th>Life Stage</th>
                <td>{data.life_stage ? data.life_stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not specified'}</td>
              </tr>
              <tr>
                <th>Family Situation</th>
                <td>{data.family_situation || 'Not specified'}</td>
              </tr>
              <tr>
                <th>Key Roles</th>
                <td>{data.key_roles || 'Not specified'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section B: Identity, Calling & Legacy */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section B: Identity, Calling & Legacy</h1>
        <p className="section-subtitle">Who are you and what are you here to do?</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.identity_statement && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Identity Statement</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.identity_statement}</p>
              </div>
            )}

            {data.proud_of && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Most Proud Of</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.proud_of}</p>
              </div>
            )}

            {data.calling_purpose && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Calling & Purpose</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.calling_purpose}</p>
              </div>
            )}

            {data.legacy_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Legacy Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.legacy_vision}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.thankful_for && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Most Thankful For</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.thankful_for}</p>
              </div>
            )}

            {data.known_for && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Want to Be Known For</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.known_for}</p>
              </div>
            )}

            {data.guiding_scripture && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Guiding Scripture</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.guiding_scripture}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section C: S.T.E.P. Reflection Model */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section C: S.T.E.P. Reflection Model</h1>
        <p className="section-subtitle">Think about what inspires and drains you across these categories: Spiritual, Things, Experiences, & People</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {/* Spiritual */}
            <div className="content-block" style={{ marginBottom: '0.15in' }}>
              <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Spiritual</h3>

              {data.step_spiritual_believe && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>I believe in... why?</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_spiritual_believe}</p>
                </div>
              )}

              {data.step_spiritual_love && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>I love to... why?</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_spiritual_love}</p>
                </div>
              )}

              {data.step_spiritual_center && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>My center is...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_spiritual_center}</p>
                </div>
              )}
            </div>

            {/* Things */}
            <div className="content-block" style={{ marginBottom: '0.15in' }}>
              <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Things</h3>

              {data.step_things_have && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>I already have...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_things_have}</p>
                </div>
              )}

              {data.step_things_want && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>I would like more of...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_things_want}</p>
                </div>
              )}

              {data.step_things_dont_need && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>I don't need...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_things_dont_need}</p>
                </div>
              )}
            </div>

            {/* Experiences */}
            <div className="content-block" style={{ marginBottom: '0.15in' }}>
              <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Experiences</h3>

              {data.step_experiences_more_time && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>If I had more time I would...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_experiences_more_time}</p>
                </div>
              )}

              {data.step_experiences_avoid && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>I want to avoid...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_experiences_avoid}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div>
            {/* People */}
            <div className="content-block" style={{ marginBottom: '0.15in' }}>
              <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>People</h3>

              {data.step_people_important && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>Most important people in my life...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_people_important}</p>
                </div>
              )}

              {data.step_people_avoid && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>Types of people to avoid...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_people_avoid}</p>
                </div>
              )}
            </div>

            {/* Core Values */}
            <div className="content-block" style={{ marginBottom: '0.15in' }}>
              <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Core Values</h3>

              {data.step_core_values_admire && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>People I admire most...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_core_values_admire}</p>
                </div>
              )}

              {data.step_core_values_traits && (
                <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#f0f9ff', border: '1px solid #dbeafe', borderRadius: '4px' }}>
                  <h4 style={{ fontSize: '8.5pt', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.03in' }}>Traits they embody...</h4>
                  <p className="text-content" style={{ fontSize: '8pt' }}>{data.step_core_values_traits}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Section D: Personal Wealth Gap */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section D: Personal Wealth Gap</h1>
        <p className="section-subtitle">Calculate what you need for financial independence</p>

        {/* Wealth Gap Summary */}
        {wealthGap && (
          <div className="content-block" style={{ padding: '0.15in', backgroundColor: '#fff7ed', border: '2px solid #fed7aa', borderRadius: '8px', marginBottom: '0.2in' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in' }}>Wealth Gap</h3>

            {(() => {
              const currentYear = new Date().getFullYear();
              const yearsUntilExit = Math.max(0, (wealthGap.exit_year || currentYear + 2) - currentYear);
              const ageAtExit = wealthGap.current_age + yearsUntilExit;

              // Calculate outside income growth
              const outsideIncomeGrowthRate = (wealthGap.outside_income_annual_increase || 0) / 100;
              const yearsUntilRetirement = Math.max(0, wealthGap.retirement_age - wealthGap.current_age);
              const futureOutsideIncomeMonthly = (wealthGap.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);

              // Calculate monthly need at exit with inflation
              const inflationMultiplier = Math.pow(1 + (wealthGap.annual_inflation_rate || 2) / 100, yearsUntilExit);
              const monthlyNeedAtExit = wealthGap.monthly_cash_need * inflationMultiplier * (1 + (wealthGap.lifestyle_change_percentage || 0) / 100);

              // Current and future net monthly needs
              const currentNetMonthlyNeed = Math.max(0, wealthGap.monthly_cash_need - (wealthGap.outside_income_monthly || 0));
              const netMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);

              // Get required sale price data
              const requiredData = wealthGap.required_sale_price_data || {};
              const hasRequiredData = wealthGap.wealth_goal_method === 'monthly_needs' && requiredData.total_capital_needed !== undefined;

              return (
                <>
                  {wealthGap.exit_year && (
                    <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: '6px', textAlign: 'center' }}>
                      <p style={{ fontSize: '8pt' }}>
                        Exit Year: <strong style={{ color: '#7c3aed' }}>{wealthGap.exit_year}</strong>
                        {wealthGap.current_age && <> @ Age <strong style={{ color: '#7c3aed' }}>{ageAtExit}</strong></>}
                      </p>
                    </div>
                  )}

                  <div style={{ backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.1in' }}>
                    <table style={{ width: '100%', fontSize: '8pt', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                          <th style={{ textAlign: 'left', padding: '0.05in', fontWeight: '600' }}></th>
                          <th style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>Current</th>
                          <th style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>At Exit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wealthGap.wealth_goal_method === 'monthly_needs' && (
                          <>
                            <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.05in', fontWeight: '600' }}>Net Monthly Needs</td>
                              <td style={{ textAlign: 'center', padding: '0.05in', color: '#2563eb', fontWeight: '600' }}>
                                {formatCurrency(currentNetMonthlyNeed)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.05in', color: '#2563eb', fontWeight: '600' }}>
                                {formatCurrency(netMonthlyNeed)}
                              </td>
                            </tr>
                            {hasRequiredData && (
                              <tr>
                                <td style={{ padding: '0.05in', fontWeight: '600' }}>Capital Required</td>
                                <td style={{ textAlign: 'center', padding: '0.05in', color: '#059669', fontWeight: '600' }}>
                                  -
                                </td>
                                <td style={{ textAlign: 'center', padding: '0.05in', color: '#059669', fontWeight: '600' }}>
                                  {formatCurrency(requiredData.total_capital_needed)}
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                        <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.05in', fontWeight: '600' }}>Personal Net Worth</td>
                          <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>
                            {formatCurrency(wealthGap.current_net_worth)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>
                            {formatCurrency(hasRequiredData ? requiredData.future_net_worth : wealthGap.current_net_worth)}
                          </td>
                        </tr>
                        {(wealthGap.future_business_value || wealthGap.desired_sale_price) && (
                          <tr style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#eff6ff' }}>
                            <td style={{ padding: '0.05in', fontWeight: '600' }}>Est. Business Exit Value</td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600', color: '#2563eb' }}>
                              {formatCurrency(wealthGap.desired_sale_price || 0)}
                            </td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600', color: '#2563eb' }}>
                              {formatCurrency(wealthGap.future_business_value || wealthGap.desired_sale_price || 0)}
                            </td>
                          </tr>
                        )}
                        {hasRequiredData && (
                          <tr style={{ borderTop: '2px solid #d1d5db', backgroundColor: requiredData.wealth_surplus > 0 ? '#f0fdf4' : '#fef2f2' }}>
                            <td style={{ padding: '0.05in', fontWeight: '700', fontSize: '8pt' }}>
                              {requiredData.wealth_surplus > 0 ? 'Wealth Surplus' : 'Gap from Business'}
                            </td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '700', fontSize: '8pt', color: requiredData.wealth_surplus > 0 ? '#059669' : '#dc2626' }}>
                              -
                            </td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '700', fontSize: '8pt', color: requiredData.wealth_surplus > 0 ? '#059669' : '#dc2626' }}>
                              {formatCurrency(requiredData.wealth_surplus > 0 ? requiredData.wealth_surplus : requiredData.net_gap_from_business)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Financial Planning Details - Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.investment_risk_tolerance && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Investment Risk Tolerance</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{normalizeRiskTolerance(data.investment_risk_tolerance)}</p>
              </div>
            )}

            {data.primary_wealth_strategy && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Primary Wealth Strategy</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{normalizeWealthStrategy(data.primary_wealth_strategy)}</p>
              </div>
            )}

            {data.financial_planning_status && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Financial Planning Status</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{normalizePlanningStatus(data.financial_planning_status)}</p>
              </div>
            )}

            {estateDocuments.length > 0 && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Estate Planning Documents</h3>
                <ul style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.4' }}>
                  {normalizeEstateDocuments(estateDocuments).map((doc, idx) => (
                    <li key={idx}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.major_financial_concerns && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Major Financial Concerns</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.major_financial_concerns}</p>
              </div>
            )}

            {sourceOfWealth.length > 0 && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Source of Wealth</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>
                  {normalizeSourceOfWealth(sourceOfWealth).join(', ')}
                  {data.source_of_wealth_other && `, ${data.source_of_wealth_other}`}
                </p>
              </div>
            )}

            {useOfWealth.length > 0 && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Use of Wealth</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>
                  {normalizeUseOfWealth(useOfWealth).join(', ')}
                  {data.use_of_wealth_other && `, ${data.use_of_wealth_other}`}
                </p>
              </div>
            )}
          </div>
        </div>

        {data.wealth_sources && (
          <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Wealth Sources</h3>
            <p className="text-content">{data.wealth_sources}</p>
          </div>
        )}

        {data.gap_closing_strategies && (
          <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Gap Closing Strategies</h3>
            <p className="text-content">{data.gap_closing_strategies}</p>
          </div>
        )}
      </div>

      {/* Section E: Financial Planning Details - Already covered in Section D */}

      {/* Section F: 10-Year Life Vision */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section F: 10-Year Life Vision</h1>
        <p className="section-subtitle">Paint a picture of your ideal life 10 years from now</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.living_location && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Where Are You Living?</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.living_location}</p>
              </div>
            )}

            {data.faith_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Faith Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.faith_vision}</p>
              </div>
            )}

            {data.marriage_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Marriage Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.marriage_vision}</p>
              </div>
            )}

            {data.health_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Health Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.health_vision}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.time_spending && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>How Are You Spending Your Time?</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.time_spending}</p>
              </div>
            )}

            {data.work_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Work Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.work_vision}</p>
              </div>
            )}

            {data.giving_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Giving Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.giving_vision}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section G: 3-5 Year Personal Picture */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section G: 3-5 Year Personal Picture</h1>
        <p className="section-subtitle">What specific transformation goals do you have?</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.marriage_transformation && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Marriage Transformation</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.marriage_transformation}</p>
              </div>
            )}

            {data.health_transformation && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Health Transformation</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.health_transformation}</p>
              </div>
            )}

            {data.work_transformation && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Work Transformation</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.work_transformation}</p>
              </div>
            )}

            {data.changes_needed && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Changes Needed</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.changes_needed}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.kids_transformation && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Kids & Family Transformation</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.kids_transformation}</p>
              </div>
            )}

            {data.spiritual_transformation && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Spiritual Transformation</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.spiritual_transformation}</p>
              </div>
            )}

            {data.net_worth_transformation && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Net Worth Transformation</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.net_worth_transformation}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section H: Perfect Week Exercise */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section H: Perfect Week Exercise</h1>
        <p className="section-subtitle">Design your ideal weekly rhythm</p>

        {/* Time Management & Rhythm Assessment */}
        {assessmentData?.category_scores?.time_management !== null &&
         assessmentData?.category_scores?.time_management !== undefined && (
          <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#f0fdfa', border: '2px solid #5eead4', borderRadius: '6px', marginBottom: '0.15in' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
              <div style={{ flexShrink: 0 }}>
                <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.time_management) }}>
                  <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.time_management), marginBottom: '0.03in' }}>
                    {formatPercentage(assessmentData.category_scores.time_management)}
                  </p>
                  <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Time Management & Rhythm</h4>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {PERSONAL_CATEGORY_DESCRIPTIONS['Time Management & Rhythm']?.summary || 'Measures how effectively you manage time and maintain healthy life rhythms.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.perfect_week_mornings && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Mornings</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.perfect_week_mornings}</p>
              </div>
            )}

            {data.perfect_week_evenings && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Evenings</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.perfect_week_evenings}</p>
              </div>
            )}

            {data.current_week_reality && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Current Week Reality</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.current_week_reality}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.perfect_week_afternoons && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Afternoons</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.perfect_week_afternoons}</p>
              </div>
            )}

            {data.perfect_week_weekends && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Weekends</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.perfect_week_weekends}</p>
              </div>
            )}

            {data.gap_analysis && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Gap Analysis</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.gap_analysis}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section I: Relationships & Family Alignment */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section I: Relationships & Family Alignment</h1>
        <p className="section-subtitle">Get on the same page with those who matter most</p>

        {/* Relationship & Family Assessment */}
        {assessmentData?.category_scores?.relational_health !== null &&
         assessmentData?.category_scores?.relational_health !== undefined &&
         assessmentData?.category_scores?.family_relationships !== null &&
         assessmentData?.category_scores?.family_relationships !== undefined && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in', marginBottom: '0.15in' }}>
            {/* Relational Health & Marriage */}
            <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#eff6ff', border: '2px solid #93c5fd', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.relational_health) }}>
                    <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.relational_health), marginBottom: '0.03in' }}>
                      {formatPercentage(assessmentData.category_scores.relational_health)}
                    </p>
                    <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Relational Health & Marriage</h4>
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                    {PERSONAL_CATEGORY_DESCRIPTIONS['Relational Health & Marriage']?.summary || 'Measures the health and strength of your marriage relationship.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Family Relationships & Legacy */}
            <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#faf5ff', border: '2px solid #d8b4fe', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.family_relationships) }}>
                    <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.family_relationships), marginBottom: '0.03in' }}>
                      {formatPercentage(assessmentData.category_scores.family_relationships)}
                    </p>
                    <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Family Relationships & Legacy</h4>
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                    {PERSONAL_CATEGORY_DESCRIPTIONS['Family Relationships & Legacy']?.summary || 'Evaluates the health and depth of your family relationships and your intentionality about family legacy.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.relationship_priorities && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Relationship Priorities</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.relationship_priorities}</p>
              </div>
            )}

            {data.spouse_alignment && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Spouse Alignment</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.spouse_alignment}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.key_conversations && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Key Conversations Needed</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.key_conversations}</p>
              </div>
            )}

            {data.family_concerns && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Family Concerns</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.family_concerns}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section J: Health, Energy & Capacity */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section J: Health, Energy & Capacity</h1>
        <p className="section-subtitle">Assess your wellbeing and plan for meaningful activities beyond work</p>

        {/* Health & Hobbies Assessment */}
        {assessmentData?.category_scores?.physical_health !== null &&
         assessmentData?.category_scores?.physical_health !== undefined &&
         assessmentData?.category_scores?.hobbies_interests !== null &&
         assessmentData?.category_scores?.hobbies_interests !== undefined && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in', marginBottom: '0.15in' }}>
            {/* Physical Health & Vitality */}
            <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.physical_health) }}>
                    <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.physical_health), marginBottom: '0.03in' }}>
                      {formatPercentage(assessmentData.category_scores.physical_health)}
                    </p>
                    <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Physical Health & Vitality</h4>
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                    {PERSONAL_CATEGORY_DESCRIPTIONS['Physical Health & Vitality']?.summary || 'Evaluates your physical health, energy levels, and vitality for life beyond your business.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Hobbies, Interests & Personal Growth */}
            <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#ecfeff', border: '2px solid #67e8f9', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.hobbies_interests) }}>
                    <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.hobbies_interests), marginBottom: '0.03in' }}>
                      {formatPercentage(assessmentData.category_scores.hobbies_interests)}
                    </p>
                    <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Hobbies, Interests & Personal Growth</h4>
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                    {PERSONAL_CATEGORY_DESCRIPTIONS['Hobbies, Interests & Personal Growth']?.summary || 'Measures your engagement with hobbies, interests, and ongoing personal development.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.health_constraints && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Health Constraints</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.health_constraints}</p>
              </div>
            )}

            {data.health_action_steps && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Health Action Steps</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.health_action_steps}</p>
              </div>
            )}

            {data.hobbies_to_develop && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Hobbies to Develop</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.hobbies_to_develop}</p>
              </div>
            )}

            {data.hobby_communities && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Hobby Communities</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.hobby_communities}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.three_year_health_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>3-Year Health Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.three_year_health_vision}</p>
              </div>
            )}

            {data.current_hobbies && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Current Hobbies</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.current_hobbies}</p>
              </div>
            )}

            {data.time_allocation_hobbies && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Time Allocation for Hobbies</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.time_allocation_hobbies}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section K: Work, Purpose & Contribution Post-Exit */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section K: Work, Purpose & Contribution Post-Exit</h1>
        <p className="section-subtitle">What will you do after you exit?</p>

        {/* Post-Exit & Transitions Assessment */}
        {assessmentData?.category_scores?.exit_vision !== null &&
         assessmentData?.category_scores?.exit_vision !== undefined &&
         assessmentData?.category_scores?.life_transition !== null &&
         assessmentData?.category_scores?.life_transition !== undefined && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in', marginBottom: '0.15in' }}>
            {/* Post-Exit Vision & Planning */}
            <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#eef2ff', border: '2px solid #a5b4fc', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.exit_vision) }}>
                    <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.exit_vision), marginBottom: '0.03in' }}>
                      {formatPercentage(assessmentData.category_scores.exit_vision)}
                    </p>
                    <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Post-Exit Vision & Planning</h4>
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                    {PERSONAL_CATEGORY_DESCRIPTIONS['Post-Exit Vision & Planning']?.summary || 'Evaluates the clarity and specificity of your vision for life after business exit.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Life Transitions & Change Readiness */}
            <div className="content-block" style={{ padding: '0.12in', backgroundColor: '#fffbeb', border: '2px solid #fcd34d', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.1in' }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ textAlign: 'center', padding: '0.08in', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(assessmentData.category_scores.life_transition) }}>
                    <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(assessmentData.category_scores.life_transition), marginBottom: '0.03in' }}>
                      {formatPercentage(assessmentData.category_scores.life_transition)}
                    </p>
                    <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Score</p>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', marginBottom: '0.04in' }}>Life Transitions & Change Readiness</h4>
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                    {PERSONAL_CATEGORY_DESCRIPTIONS['Life Transitions & Change Readiness']?.summary || 'Measures your emotional and psychological preparedness for major life transitions, including business exit.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.work_intentions && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Work Intentions</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.work_intentions}</p>
              </div>
            )}

            {data.avoiding_retire_die && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Avoiding "Retire & Die"</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.avoiding_retire_die}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.contribution_focus && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Contribution Focus</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.contribution_focus}</p>
              </div>
            )}

            {data.meaningful_work_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Meaningful Work Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.meaningful_work_vision}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section L: Faith, Generosity & Eternal Impact */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section L: Faith, Generosity & Eternal Impact</h1>
        <p className="section-subtitle">How will you steward your resources for eternal impact?</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.spiritual_growth_goals && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Spiritual Growth Goals</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.spiritual_growth_goals}</p>
              </div>
            )}

            {data.giving_plan && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Giving Plan</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.giving_plan}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.spiritual_practices && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Spiritual Practices</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.spiritual_practices}</p>
              </div>
            )}

            {data.eternal_impact_vision && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Eternal Impact Vision</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.eternal_impact_vision}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section M: Five Ds (Personal) */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section M: Five Ds (Personal)</h1>
        <p className="section-subtitle">Personal protection for Death, Disability, Divorce, Distress, Disagreement</p>

        {/* Death */}
        <div className="content-block" style={{ marginBottom: '0.15in' }}>
          <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Death</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
            {/* Left Column - Checklist */}
            <div>
              {fiveDsPersonalDeathItems.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '8pt', fontWeight: '600', color: '#374151', marginBottom: '0.05in' }}>Checklist Items:</h4>
                  <ul style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.4' }}>
                    {fiveDsPersonalDeathItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Right Column - Text Field */}
            <div>
              {data.five_ds_personal_death && (
                <div style={{ padding: '0.1in', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #dbeafe' }}>
                  <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.five_ds_personal_death}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Disability */}
        <div className="content-block" style={{ marginBottom: '0.15in' }}>
          <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Disability</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
            {/* Left Column - Checklist */}
            <div>
              {fiveDsPersonalDisabilityItems.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '8pt', fontWeight: '600', color: '#374151', marginBottom: '0.05in' }}>Checklist Items:</h4>
                  <ul style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.4' }}>
                    {fiveDsPersonalDisabilityItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Right Column - Text Field */}
            <div>
              {data.five_ds_personal_disability && (
                <div style={{ padding: '0.1in', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #dbeafe' }}>
                  <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.five_ds_personal_disability}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Divorce */}
        <div className="content-block" style={{ marginBottom: '0.15in' }}>
          <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Divorce</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
            {/* Left Column - Checklist */}
            <div>
              {fiveDsPersonalDivorceItems.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '8pt', fontWeight: '600', color: '#374151', marginBottom: '0.05in' }}>Checklist Items:</h4>
                  <ul style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.4' }}>
                    {fiveDsPersonalDivorceItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Right Column - Text Field */}
            <div>
              {data.five_ds_personal_divorce && (
                <div style={{ padding: '0.1in', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #dbeafe' }}>
                  <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.five_ds_personal_divorce}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Distress */}
        <div className="content-block" style={{ marginBottom: '0.15in' }}>
          <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Distress (Financial/Personal)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
            {/* Left Column - Checklist */}
            <div>
              {fiveDsPersonalDistressItems.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '8pt', fontWeight: '600', color: '#374151', marginBottom: '0.05in' }}>Checklist Items:</h4>
                  <ul style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.4' }}>
                    {fiveDsPersonalDistressItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Right Column - Text Field */}
            <div>
              {data.five_ds_personal_distress && (
                <div style={{ padding: '0.1in', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #dbeafe' }}>
                  <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.five_ds_personal_distress}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Disagreement */}
        <div className="content-block" style={{ marginBottom: '0.15in' }}>
          <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '2px solid #dbeafe', paddingBottom: '0.05in' }}>Disagreement (Family Conflict)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
            {/* Left Column - Checklist */}
            <div>
              {fiveDsPersonalDisagreementItems.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '8pt', fontWeight: '600', color: '#374151', marginBottom: '0.05in' }}>Checklist Items:</h4>
                  <ul style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.4' }}>
                    {fiveDsPersonalDisagreementItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* Right Column - Text Field */}
            <div>
              {data.five_ds_personal_disagreement && (
                <div style={{ padding: '0.1in', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #dbeafe' }}>
                  <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.five_ds_personal_disagreement}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {data.five_ds_personal_priorities && (
          <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Priority Actions</h3>
            <p className="text-content">{data.five_ds_personal_priorities}</p>
          </div>
        )}
      </div>

      {/* Section N: 90-Day Personal Focus */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section N: 90-Day Personal Focus</h1>
        <p className="section-subtitle">What personal priorities will you tackle in the next 90 days?</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {personalSprintPriorities && personalSprintPriorities.some(p => p) && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Top 3 Personal Priorities (90-Day Sprint)</h3>
                <ol style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.6' }}>
                  {personalSprintPriorities.map((priority, idx) => (
                    priority && <li key={idx}>{priority}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.conversations_to_have && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Key Conversations to Have</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.conversations_to_have}</p>
              </div>
            )}

            {personalQuickWins && personalQuickWins.some(w => w) && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Quick Wins (30-60 Days)</h3>
                <ol style={{ marginLeft: '0.2in', fontSize: '8.5pt', lineHeight: '1.6' }}>
                  {personalQuickWins.map((win, idx) => (
                    win && <li key={idx}>{win}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section O: Integration with Business Plan */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section O: Integration with Business Plan</h1>
        <p className="section-subtitle">How do your personal and business plans align?</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in' }}>
          {/* Left Column */}
          <div>
            {data.alignment_check && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Personal/Business Alignment Check</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.alignment_check}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div>
            {data.conflicts_to_resolve && (
              <div className="content-block" style={{ padding: '0.12in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid', marginBottom: '0.15in' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700', borderBottom: '1px solid #bfdbfe', paddingBottom: '0.05in' }}>Conflicts to Resolve</h3>
                <p className="text-content" style={{ fontSize: '8.5pt' }}>{data.conflicts_to_resolve}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintPersonalEnvisioning;
